// src/SurveyProgressOverlay.jsx
import React, { useState, useEffect } from "react";

export const formatSurveyDate = (raw) => {
  if (!raw && raw !== 0) return "";
  const fmt = (date) => {
    if (!date || isNaN(date.getTime())) return "";
    const dd = String(date.getDate()).padStart(2, "0");
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const yyyy = String(date.getFullYear());
    return `${dd}-${mm}-${yyyy}`;
  };
  if (raw && typeof raw === "object" && (raw.seconds !== undefined || raw._seconds !== undefined)) {
    const seconds = raw.seconds ?? raw._seconds;
    const millis = Number(seconds) * 1000;
    const date = new Date(millis);
    if (!isNaN(date.getTime())) return fmt(date);
    return String(raw);
  }
  if (raw instanceof Date) {
    if (isNaN(raw.getTime())) return String(raw);
    return fmt(raw);
  }
  if (typeof raw === "number") {
    const asMs = raw < 1e12 ? raw * 1000 : raw;
    const date = new Date(asMs);
    if (!isNaN(date.getTime())) return fmt(date);
    return String(raw);
  }
  if (typeof raw === "string" && /^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    const [y, m, d] = raw.split("-").map(Number);
    const date = new Date(y, m - 1, d);
    if (!isNaN(date.getTime())) return fmt(date);
    return raw;
  }
  if (typeof raw === "string") {
    const parsed = new Date(raw);
    if (!isNaN(parsed.getTime())) return fmt(parsed);
    const parts = raw.split(/\s+/);
    if (parts.length >= 4) { /* ... date parsing fallback ... */ }
    return raw;
  }
  try { return String(raw); } catch (err) { return ""; }
};

// Helper to parse JWT to get email (for loggedBy logic)
const parseJwtEmail = (token) => {
  if (!token) return null;
  try {
    const parts = token.split(".");
    if (parts.length < 2) return null;
    const payload = JSON.parse(atob(parts[1].replace(/-/g, "+").replace(/_/g, "/")));
    return payload.email || payload.sub || null;
  } catch (e) {
    return null;
  }
};

// Helper to determine who is performing the action
const resolveLoggedBy = (latestSurvey) => {
  // Priority: 1. LocalStorage (Current User) -> 2. Token -> 3. Latest Survey
  // We prioritize LocalStorage here because "Logged By" should reflect who is *currently* acting.
  const fromLocal = localStorage.getItem("email");
  if (fromLocal) return String(fromLocal).trim();
  
  const token = localStorage.getItem("token");
  const fromToken = parseJwtEmail(token);
  if (fromToken) return String(fromToken).trim();

  const fromLatest = latestSurvey?.email ?? latestSurvey?.pilot_email ?? null;
  if (fromLatest) return String(fromLatest).trim();
  
  return "";
};

// --- MAIN COMPONENT ---

export default function SurveyProgressOverlay({ 
  title = "Survey Progress: —", 
  activeStep = 0, 
  steps, 
  surveyTime, 
  overlayRef, 
  history = [],
  latestSurvey,
  locationName,
  apiBase,
  onSurveyUpdated 
}) {
  const s = steps ?? ["Requested", "Surveyed", "Processed", "Uploaded", "Verified"];
  const max = s.length;

  // --- LOCAL STATE FOR MODAL & FORM ---
  const [updateModalOpen, setUpdateModalOpen] = useState(false);
  const [updateSubmitting, setUpdateSubmitting] = useState(false);
  const [pilotName, setPilotName] = useState("");
  const [pilotEmail, setPilotEmail] = useState("");
  const [surveyDateInput, setSurveyDateInput] = useState("");
  const [remarksInput, setRemarksInput] = useState("");
  const [loggedByComputed, setLoggedByComputed] = useState("");
  
  // --- HOVER STATE FOR DOTS ---
  const [hoveredIdx, setHoveredIdx] = useState(null);
  const [hoveredPayload, setHoveredPayload] = useState(null);

  // --- CSS INJECTION ---
  const injectedStyles = `
    .status-dot { box-sizing: border-box; transition: background 200ms ease, transform 140ms ease; user-select: none; display: inline-flex; align-items: center; justify-content: center; }
    .status-requested { background: #2563eb; } 
    .status-surveyed { background: #10b981; } 
    .status-processed { background: #f59e0b; } 
    .status-uploaded { background: #7c3aed; } 
    .status-verified { background: #6b7280; } 
    .status-cancelled { background: #ef4444; } 
    .status-empty { background: rgba(255,255,255,0.95); color: #0f172a !important; border: 1px solid rgba(2,6,23,0.06); }
    .status-dot:hover { transform: translateY(-2px); }
  `;

  // --- PROGRESS BAR LOGIC ---
  const normalizeDateKey = (raw) => {
    if (!raw) return null;
    const d = new Date(raw);
    if(!isNaN(d.getTime())) return d.toISOString().split('T')[0];
    return raw;
  };

  const timeOf = (x) => {
    const t = x.progress_time ?? x.progressTime ?? x.updated_at ?? x.modified_at ?? x.timestamp ?? x.created_at ?? null;
    if (!t) return 0;
    const n = isNaN(Date.parse(t)) ? Number(t) : Date.parse(t);
    return isNaN(n) ? 0 : n;
  };

  const targetSurveyKey = normalizeDateKey(surveyTime);

  const statusMatches = (h, st) => {
    const rowStatus = (h.status ?? h.state ?? "").toString().toLowerCase();
    return rowStatus === String(st || "").toLowerCase();
  };

  const matchedPerStatus = {};
  for (const label of s) matchedPerStatus[label] = null;

  if (Array.isArray(history) && history.length > 0) {
    let rowsForSurvey = history;
    if (targetSurveyKey) {
      rowsForSurvey = history.filter((h) => {
        try { return normalizeDateKey(h.survey_time ?? h.survey_date) === targetSurveyKey; } catch (e) { return false; }
      });
    }
    if(rowsForSurvey.length === 0 && !targetSurveyKey) rowsForSurvey = history;

    for (const label of s) {
      const st = label.toString().toLowerCase();
      const cand = rowsForSurvey.filter((h) => statusMatches(h, st));
      if (cand.length > 0) {
        cand.sort((a, b) => timeOf(b) - timeOf(a));
        matchedPerStatus[label] = cand[0];
      }
    }
  }

  const hasAnyMatched = Object.values(matchedPerStatus).some(Boolean);
  let highestCompletedIdx = -1;
  s.forEach((label, idx) => {
    if (matchedPerStatus[label]) highestCompletedIdx = Math.max(highestCompletedIdx, idx);
  });

  if (!hasAnyMatched && !targetSurveyKey) {
    // keep -1
  } else if (highestCompletedIdx === -1) {
    highestCompletedIdx = Math.max(0, Math.min(activeStep, s.length - 1));
  }

  const pctFilled = max > 1 && highestCompletedIdx >= 0 ? (highestCompletedIdx / (max - 1)) * 98 : 0;
  const statusKeyFromLabel = (label) => String(label || "").toLowerCase().trim().replace(/\s+/g, "-").replace(/[^a-z0-9\-]/g, "");
  const loggedByEmail = (row) => row?.email ?? row?.logged_email ?? row?.pilot_email ?? row?.user_email ?? null;
  
  const getNormalizedStatus = (p) => {
    if (!p) return "";
    const val = p.status || p.latest_status || p.state || "";
    return String(val).toLowerCase();
  };
  
  const currentStatus = getNormalizedStatus(hoveredPayload);
  const hoveredIsVerified = currentStatus.includes("verified");
  const verifiedExists = Object.values(matchedPerStatus).some(r => r && (r.status||"").toLowerCase().includes("verified"));
  const finalIsVerified = hoveredIsVerified || verifiedExists;


  // --- MODAL & SUBMISSION LOGIC ---

  const computeNextStatus = () => {
    const current = (latestSurvey?.status || "Requested").toString();
    const idx = Math.max(0, s.findIndex((step) => step.toLowerCase() === current.toLowerCase()));
    const nextIndex = Math.min(idx + 1, s.length - 1);
    return s[nextIndex];
  };

  const handleOpenModal = () => {
    // UPDATED: Start FRESH. Do not prefill from latestSurvey.
    setPilotName(""); 
    setPilotEmail("");
    setRemarksInput("");
    
    // Resolve the current logged-in user (keep this)
    setLoggedByComputed(resolveLoggedBy(latestSurvey));
    
    // Default date to Today
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, "0");
    const dd = String(today.getDate()).padStart(2, "0");
    setSurveyDateInput(`${yyyy}-${mm}-${dd}`);

    setUpdateModalOpen(true);
  };

  const handleCloseModal = () => {
    setUpdateModalOpen(false);
    setUpdateSubmitting(false);
  };

  const submitUpdateModal = async (ev) => {
    ev?.preventDefault?.();

    if (!pilotName?.trim()) {
      alert("Pilot Name is required.");
      return;
    }
    if (!surveyDateInput) {
      alert("Progress Date is required.");
      return;
    }
    if (!locationName) {
      alert("Unable to determine a valid location_name. Please re-select the project.");
      return;
    }

    setUpdateSubmitting(true);

    const latestStatus = (latestSurvey?.status || "Requested").toString();
    let nextStatusForCreate;
    if (latestStatus.toLowerCase() === "requested") {
      nextStatusForCreate = "Surveyed";
    } else {
      nextStatusForCreate = computeNextStatus();
    }

    const actorEmail = (loggedByComputed && String(loggedByComputed).trim()) || null;
    const organization = latestSurvey?.organization ?? localStorage.getItem("organization") ?? null;

    const payload = {
      location_name: String(locationName),
      status: nextStatusForCreate,
      pilot_name: pilotName.trim() || null,
      pilot_email: pilotEmail?.trim() || null,
      survey_time: latestSurvey?.survey_time ?? null,
      progress_time: surveyDateInput || null,
      remark: remarksInput?.trim() || null,
      email: actorEmail || (latestSurvey?.email ?? localStorage.getItem("email") ?? null),
      organization: organization || null,
    };

    try {
      const token = localStorage.getItem("token") || null;
      const headers = { "Content-Type": "application/json" };
      if (token) headers.Authorization = `Bearer ${token}`;

      const baseUrl = apiBase ? apiBase.replace(/\/+$/, "") : "";
      const res = await fetch(`${baseUrl}/survey_request`, {
        method: "POST",
        headers,
        body: JSON.stringify(payload),
      });

      const txt = await res.text().catch(() => "");
      let json = null;
      try { json = txt ? JSON.parse(txt) : null; } catch (e) {}

      if (!res.ok) {
        const serverMessage = (json && (json.message || json.error)) ? (json.message || json.error) : txt || `HTTP ${res.status}`;
        console.error("Server error:", res.status, serverMessage);
        alert(`Failed to update status: ${serverMessage}`);
        setUpdateSubmitting(false);
        return;
      }

      const created = (json && json.survey) ? json.survey : json ?? null;
      
      if (onSurveyUpdated) {
        onSurveyUpdated(created || { ...(latestSurvey||{}), ...payload });
      }

      window.dispatchEvent(new CustomEvent("survey-request-created", { 
        detail: created || { ...(latestSurvey||{}), ...payload } 
      }));

      handleCloseModal();

    } catch (err) {
      console.error("Network error creating survey request:", err);
      alert("Failed to create survey request. See console.");
      setUpdateSubmitting(false);
    }
  };

  // --- RENDER ---

  return (
    <>
      {/* THE OVERLAY BAR */}
      <div
        ref={overlayRef}
        aria-hidden={false}
        style={{
          position: "absolute",
          top: 12, left: 12, right: 12,
          // FIX: Increased zIndex to 5000 to sit above Leaflet controls (which are ~1000)
          zIndex: 5000, 
          display: "flex", alignItems: "center", gap: 12,
          padding: 12, borderRadius: 8,
          background: "#07090cff",
          boxShadow: "0 8px 30px rgba(2,6,23,0.45)",
          color: "#fff", pointerEvents: "auto",
        }}
      >
        <style>{injectedStyles}</style>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: "#fff", whiteSpace: "nowrap" }}>{title}</div>
          </div>

          {surveyTime ? (
            <div style={{ marginTop: 6, fontSize: 13, color: "rgba(255,255,255,0.95)" }}>
              <strong>Survey date:</strong> <span style={{ marginLeft: 8 }}>{formatSurveyDate(surveyTime)}</span>
            </div>
          ) : null}

          <div style={{ marginTop: 15, marginBottom: 10 }}>
            <div style={{ position: "relative", height: 36, display: "flex", alignItems: "center" }}>
              <div style={{ position: "absolute", left: 2, right: 0, height: 6, borderRadius: 999, background: "rgba(255,255,255,0.12)", top: "50%", transform: "translateY(-50%)" }} />
              <div style={{ position: "absolute", left: 15, width: `${pctFilled}%`, height: 6, borderRadius: 999, background: "#1e40af", top: "50%", transform: "translateY(-50%)", transition: "width 300ms ease", overflow: "hidden" }} />
              
              <div style={{ position: "relative", display: "flex", justifyContent: "space-between", width: "100%", padding: "0 0px" }}>
                {s.map((label, idx) => {
                  const matched = matchedPerStatus?.[label];
                  const allowFallbackToActive = hasAnyMatched || !!targetSurveyKey;
                  const isCompleted = !!matched || (allowFallbackToActive && idx <= activeStep);
                  const isActive = idx === activeStep;
                  const statusKey = statusKeyFromLabel(label);
                  const dotClass = isCompleted ? `status-dot status-${statusKey}` : "status-dot status-empty";
                  const payload = matched ?? null;

                  return (
                    <div
                      key={label}
                      style={{ display: "flex", flexDirection: "column", alignItems: "center", minWidth: 0, position: "relative" }}
                      onMouseEnter={() => { setHoveredIdx(idx); setHoveredPayload(payload || null); }}
                      onMouseLeave={() => { setHoveredIdx(null); setHoveredPayload(null); }}
                    >
                      <div title={label} className={dotClass} style={{ width: 28, height: 28, borderRadius: 999, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 700, color: isCompleted ? "#fff" : "#0f172a", boxShadow: isActive ? "0 6px 18px rgba(14,165,166,0.16)" : "none", zIndex: 2, cursor: payload ? "pointer" : "default" }}>
                        {isCompleted ? <i className="bi bi-check-lg" style={{ fontSize: 12 }} /> : idx + 1}
                      </div>
                      <div style={{ marginTop: 6, fontSize: 12, color: "rgba(255,255,255,0.9)", opacity: 0.95, whiteSpace: "nowrap", transform: "translateY(1px)" }}>
                        <span style={{ background: "rgba(255,255,255,0.06)", padding: "4px 8px", borderRadius: 6 }}>{label}</span>
                      </div>

                      {hoveredIdx === idx && hoveredPayload ? (
                        <div role="dialog" style={{ position: "absolute", top: "calc(100% + 10px)", left: "50%", transform: "translateX(-50%)", width: 300, maxWidth: "min(90vw, 320px)", background: "#ffffff", color: "#0f172a", borderRadius: 8, padding: 12, boxShadow: "0 10px 30px rgba(2,6,23,0.2)", zIndex: 40, textAlign: "left", pointerEvents: "auto" }}>
                          <div style={{ fontWeight: 800, marginBottom: 6 }}>{label}</div>
                          <div style={{ fontSize: 13, marginBottom: 8 }}>
                            <div style={{ fontWeight: 600 }}>Logged by</div>
                            <div style={{ color: "#374151" }}>{loggedByEmail(hoveredPayload) || hoveredPayload.logged_by || "—"}</div>
                            {hoveredPayload.logged_at || hoveredPayload.timestamp ? (
                               <div style={{ marginTop: 6, fontSize: 12, color: "#6b7280" }}>{formatSurveyDate(hoveredPayload.logged_at || hoveredPayload.timestamp)}</div>
                            ) : null}
                          </div>
                          {(hoveredPayload.pilot_name || hoveredPayload.pilot_email) && (
                             <div style={{ fontSize: 13 }}>
                               {hoveredPayload.pilot_name && <div><b>Pilot:</b> {hoveredPayload.pilot_name}</div>}
                             </div>
                          )}
                          {hoveredPayload.remark && (
                            <div style={{ marginTop: 6, fontSize: 13 }}>
                              <div style={{ fontWeight: 600 }}>Remark</div>
                              <div style={{ color: "#374151", marginTop: 4 }}>{hoveredPayload.remark}</div>
                            </div>
                          )}
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        <div style={{ marginLeft: "12px", display: "flex", alignItems: "center", gap: 8 }}>
          <button
            type="button"
            onClick={handleOpenModal}
            disabled={finalIsVerified}
            style={{
              display: "inline-flex", alignItems: "center", gap: 8,
              padding: "8px 12px", borderRadius: 8,
              background: finalIsVerified ? "rgba(156,163,175,0.5)" : "#10b981",
              border: "none", color: "#fff", fontWeight: 700,
              cursor: finalIsVerified ? "not-allowed" : "pointer",
              boxShadow: finalIsVerified ? "none" : "0 6px 18px rgba(16,185,129,0.12)",
              pointerEvents: finalIsVerified ? "none" : "auto",
              opacity: finalIsVerified ? 0.7 : 1,
              transition: "all 0.15s ease",
            }}
          >
            <i className="bi bi-pencil-square" /> Update Status
          </button>
        </div>
      </div>

      {/* --- MODAL OVERLAY --- */}
      {updateModalOpen && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="update-status-title"
          onMouseDown={(e) => { if (e.target === e.currentTarget) handleCloseModal(); }}
          style={{
            position: "fixed", inset: 0, display: "flex", alignItems: "center", justifyContent: "center",
            padding: 20, background: "rgba(10,12,18,0.45)", pointerEvents: "auto", zIndex: 2147483647,
          }}
        >
          <div
            onMouseDown={(e) => e.stopPropagation()}
            style={{ width: 420, maxWidth: "100%", background: "#fff", borderRadius: 8, boxShadow: "0 18px 50px rgba(2,6,23,0.45)", overflow: "hidden" }}
          >
            <div style={{ padding: "14px 18px", background: "#f6f8fb", borderBottom: "1px solid #e6e9ef", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <h3 id="update-status-title" style={{ margin: 0, fontSize: 16, color: "#0f172a" }}>
                Update Status to '{computeNextStatus()}'
              </h3>
              <button type="button" onClick={handleCloseModal} aria-label="Close" style={{ background: "transparent", border: "none", cursor: "pointer", color: "#475569", fontSize: 18 }}>
                <i className="bi bi-x-lg" />
              </button>
            </div>

            <form onSubmit={submitUpdateModal}>
              <div style={{ padding: 18 }}>
                <label style={{ display: "block", marginBottom: 6, color: "#374151", fontSize: 13 }}>Pilot Name *</label>
                <input 
                    type="text" 
                    required 
                    value={pilotName} 
                    placeholder="Enter pilot name"
                    onChange={(e) => setPilotName(e.target.value)} 
                    style={{ width: "100%", padding: "10px 12px", borderRadius: 6, border: "1px solid #d1d5db", marginBottom: 12 }} 
                />

                <label style={{ display: "block", marginBottom: 6, color: "#374151", fontSize: 13 }}>Pilot Email</label>
                <input 
                    type="email" 
                    value={pilotEmail} 
                    placeholder="Enter pilot email"
                    onChange={(e) => setPilotEmail(e.target.value)} 
                    style={{ width: "100%", padding: "10px 12px", borderRadius: 6, border: "1px solid #d1d5db", marginBottom: 12 }} 
                />

                <label style={{ display: "block", marginBottom: 6, color: "#374151", fontSize: 13 }}>Progress Date *</label>
                <input 
                    type="date" 
                    required 
                    value={surveyDateInput} 
                    onChange={(e) => setSurveyDateInput(e.target.value)} 
                    style={{ width: "100%", padding: "10px 12px", borderRadius: 6, border: "1px solid #d1d5db", marginBottom: 12 }} 
                />

                <label style={{ display: "block", marginBottom: 6, color: "#374151", fontSize: 13 }}>Remarks</label>
                <textarea 
                    rows={4} 
                    value={remarksInput} 
                    placeholder="Enter any additional remarks..."
                    onChange={(e) => setRemarksInput(e.target.value)} 
                    style={{ width: "100%", padding: "10px 12px", borderRadius: 6, border: "1px solid #d1d5db", marginBottom: 12 }} 
                />

                <label style={{ display: "block", marginBottom: 6, color: "#374151", fontSize: 13 }}>Your Email (Logged By)</label>
                <div style={{ width: "100%", padding: "10px 12px", borderRadius: 6, border: "1px solid #e6e9ef", marginBottom: 4, background: "#f8fafc", color: "#0f172a" }}>
                  {loggedByComputed || <span style={{ color: "#9ca3af" }}>(not signed in)</span>}
                </div>
                <div style={{ marginBottom: 12, fontSize: 12, color: "#6b7280" }}>This will be recorded as the actor performing the update.</div>
              </div>

              <div style={{ padding: "12px 18px", display: "flex", gap: 8, justifyContent: "flex-end", borderTop: "1px solid #eceef4", background: "#fff" }}>
                <button type="button" onClick={handleCloseModal} disabled={updateSubmitting} style={{ padding: "8px 14px", borderRadius: 6, background: "#f3f4f6", border: "1px solid #e6e9ef", color: "#475569", cursor: "pointer", fontWeight: 600 }}>Cancel</button>
                <button type="submit" disabled={updateSubmitting} style={{ padding: "8px 14px", borderRadius: 6, background: updateSubmitting ? "#9bb4ff" : "#2563eb", border: "none", color: "#fff", cursor: "pointer", fontWeight: 700 }}>
                  {updateSubmitting ? "Posting…" : `Confirm and Update`}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}